import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  EditableColumnDef,
  EditableTable,
} from "../../../../../../shared/components/table/inline-edit-table";
import { CustomSelect, CustomTextField } from "../../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import { useFormActions } from "../../../../../../shared/hooks/form-builder.hooks";
import { dtrackItService } from "../../../../tc-service/dtrackit.service";
import { Utills } from "../../../../../../shared/app-configs/utilit";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";

export interface IInputTcProductStandardDetails {
  standardLabelGrade: string;
  certifiedWeight: string;
  certifiedWeightUsed: string;
}

interface InputTcProductStandardDetailsProps {
  productStandardData: IInputTcProductStandardDetails[];
  setProductStandardData: React.Dispatch<
    React.SetStateAction<IInputTcProductStandardDetails[]>
  >;
}

// Define Yup validation schema for each row
const validationSchema = Yup.object().shape({
  standardLabelGrade: Yup.string().nullable(),
  certifiedWeight: Yup.string()
    .test(
      'is-exactly-two-decimal-places',
      'Number must be a valid decimal number with upto 2 decimal places.',
      (value: string | undefined) => {
        if (!value) return true; // Allow empty values for required validation to handle
        return /^(0|[1-9]\d*|0\.\d{1,2}|[1-9]\d*\.\d{1,2})$/.test(value); // Match exactly two decimal places
      }
    )
    .typeError("Certified Weight must be a valid number"),
  certifiedWeightUsed: Yup.string()
    .test(
      'is-exactly-two-decimal-places',
      'Number must be a valid decimal number with upto 2 decimal places.',
      (value: string | undefined) => {
        if (!value) return true; // Allow empty values for required validation to handle
        return /^(0|[1-9]\d*|0\.\d{1,2}|[1-9]\d*\.\d{1,2})$/.test(value); // Match exactly two decimal places
      }
    )
    .typeError("Certified Weight Used must be a valid number"),
});

const InputTcProductStandardDetails: React.FC<InputTcProductStandardDetailsProps> = ({
  productStandardData,
  setProductStandardData,
}) => {
  const [data, setData] = useState<IInputTcProductStandardDetails[]>(productStandardData);
  const [tableData, setTableData] = useState<IInputTcProductStandardDetails[]>(productStandardData);
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const { updateField } = useFormActions("input-tc-product-details");
  const [labelGrades, setLabelGrades] = useState([]);
  const { isRestrictPopUpSave, setRestrictPopUpSave, getLabelGrades } = useTransactionCertificateStore();

  const formik = useFormik<IInputTcProductStandardDetails>({
    initialValues: {
      standardLabelGrade: "",
      certifiedWeight: "",
      certifiedWeightUsed: "",
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {
      // Submission logic if needed
    },
  });
  useEffect(() => {
    setLabelGrades(getLabelGrades);

  }, [])
  useEffect(() => {
    updateField("tc-std-info", data)
  }, [data])
  const handleCellChange = (
    rowIndex: number,
    columnId: string,
    value: any
  ) => {
    formik.setFieldValue(columnId, value);
    setData((prevData) =>
      prevData.map((row, index) =>
        index === rowIndex ? { ...row, [columnId]: value } : row
      )
    );
  };

  const handleAddNewRow = () => {
    const newRow: IInputTcProductStandardDetails = {
      standardLabelGrade: "",
      certifiedWeight: "",
      certifiedWeightUsed: "",
    };
    setData((prevData) => [...prevData, newRow]);
    setRestrictPopUpSave(true);
    setEditingRowIndex(data.length);
    formik.setValues(newRow);
    formik.setTouched({});
  };

  const handleCancelEdit = async () => {
    if(editingRowIndex!==-1){
      setData(tableData);
    }
    // const currentRowValues = formik.values;
    // const isNewRow = editingRowIndex === data.length - 1;

    // try {
    //   // Validate the row using the Formik validation schema
    //   await validationSchema.validate(currentRowValues, { abortEarly: false });

    //   if (isNewRow) {
    //     // If validation passes and it's a new row, keep it in the data
    //     setData((prevData) => prevData.map((row, index) => (index === editingRowIndex ? currentRowValues : row)));
    //   } else {
    //     // If it's an existing row, revert changes
    //     formik.setValues(data[editingRowIndex]);
    //   }
    // } catch (validationErrors) {
    //   if (isNewRow) {
    //     // If validation fails and it's a new row, remove it from the data
    //     setData((prevData) => prevData.slice(0, -1));
    //   } else {
    //     // If it's an existing row, revert changes
    //     formik.setValues(data[editingRowIndex]);
    //   }
    // }
    // Reset editing state and form
    setRestrictPopUpSave(false);
    setEditingRowIndex(-1);
    formik.resetForm();
  };

  const handleSaveRow = () => {
    if (editingRowIndex === null || !formik.isValid) return;
    if (formik.values.certifiedWeight == "" && formik.values.certifiedWeightUsed == "" && formik.values.standardLabelGrade == "") {
      return;
    }
    const updatedRow = formik.values;
    const newData = data.map((row, index) =>
        index === editingRowIndex ? updatedRow : row
      );
    setData(newData);
    setTableData(newData);
    setRestrictPopUpSave(false);
    setProductStandardData(data);
    setEditingRowIndex(-1);
    formik.resetForm();
  };

  const columns: EditableColumnDef<IInputTcProductStandardDetails>[] = [
    {
      accessorKey: "standardLabelGrade",
      header: "Standard Label Grade",
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          label=""
          name="standardLabelGrade"
          value={value}
          enableSearch={true}
          options={labelGrades}
          onChange={(e) => {
            onChange(e.target.value)
          }}
        />
      ),
    },
    {
      accessorKey: "certifiedWeight",
      header: "TC Certified Weight (kg)",
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          sx={{ mb: 2 }}
          name="certifiedWeight"
          value={value}
          onChange={(e) => {
            const newValue = e.target.value;
            // Only allow numbers with max 2 decimal places
            const regex = /^\d*\.?\d{0,2}$/;
            if (regex.test(newValue) || newValue === '') {
              onChange(newValue);
            }
            // If regex doesn't match, the value won't be updated
          }}
          placeholder="Enter value"
        />
      ),
    },
    {
      accessorKey: "certifiedWeightUsed",
      header: "Certified Weight Used (kg)",
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          sx={{ mb: 2 }}
          name="certifiedWeightUsed"
          value={value}
          onChange={(e) => {
            const newValue = e.target.value;
            // Only allow numbers with max 2 decimal places
            const regex = /^\d*\.?\d{0,2}$/;
            if (regex.test(newValue) || newValue === '') {
              onChange(newValue);
            }
            // If regex doesn't match, the value won't be updated
          }}
          placeholder="Enter value"
        />
      ),
    },
  ];

  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    setRestrictPopUpSave(true);
    formik.setValues(data[rowIndex]);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter((_, index) => index !== rowIndex);
    setData(updatedData);
    setTableData(updatedData);
    setProductStandardData(updatedData);
  };

  return (
    <div>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 1, border: "2px solid rgba(128, 128, 128, .2)", my: 4 }}
      >
        <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
          <StyledButton disabled={isRestrictPopUpSave} onClick={handleAddNewRow} sx={{ mb: 2 }}>
            Add New Row
          </StyledButton>
        </Box>
        <EditableTable
          data={data}
          columns={columns}
          onSaveRow={handleSaveRow}
          onCellChange={handleCellChange}
          editingRowIndex={editingRowIndex}
          onEditRow={handleEditRow}
          onCancelEdit={handleCancelEdit}
          onDeleteRow={handleDeleteRow}
        />
      </Box>
    </div>
  );
};

export default InputTcProductStandardDetails;
